/**
 * @typedef {Object} UserProfileSettings
 * @property {string} favoriteColor - Hex color code for profile theme
 * @property {'light' | 'dark'} theme - UI theme preference
 * @property {boolean} soundEnabled - Whether sound effects are enabled
 * @property {Object} [additional] - Additional custom settings
 */

/**
 * @typedef {Object} GameProgress
 * @property {number} level - Current level in the game
 * @property {number} score - Total score
 * @property {Object} achievements - Unlocked achievements
 * @property {Date} lastPlayed - Last played timestamp
 */

/**
 * @typedef {Object} UserProfile
 * @property {string} id - Unique identifier for the profile
 * @property {string} userId - Firebase user ID
 * @property {string} name - Display name for the profile
 * @property {string} [avatar] - URL to profile avatar image
 * @property {number} [age] - User's age
 * @property {string} [gender] - User's gender ('boy', 'girl')
 * @property {string} createdAt - ISO timestamp of profile creation
 * @property {UserProfileSettings} settings - Profile settings
 * @property {Object.<string, GameProgress>} gameProgress - Progress for each game
 */

/**
 * Creates a new user profile with default values
 * @param {string} userId - Firebase user ID
 * @param {Object} data - Initial profile data
 * @param {string} data.name - Profile name
 * @param {string} [data.avatar] - Profile avatar URL
 * @param {number} [data.age] - User's age
 * @param {string} [data.gender] - User's gender
 * @param {Partial<UserProfileSettings>} [data.settings] - Optional profile settings
 * @returns {UserProfile}
 */
export const createUserProfile = (userId, data) => {
  return {
    id: Date.now().toString(),
    userId,
    name: data.name,
    avatar: data.avatar || null,
    age: data.age || null,
    gender: data.gender || null,
    createdAt: new Date().toISOString(),
    settings: {
      favoriteColor: "#4299E1",
      theme: "light",
      soundEnabled: true,
      ...data.settings,
    },
    gameProgress: {},
  };
};

/**
 * Validates a user profile object
 * @param {UserProfile} profile - Profile to validate
 * @returns {{ isValid: boolean, errors: string[] }}
 */
export const validateUserProfile = (profile) => {
  const errors = [];

  if (!profile.id) errors.push("Profile ID is required");
  if (!profile.userId) errors.push("User ID is required");
  if (!profile.name) errors.push("Profile name is required");
  if (!profile.createdAt) errors.push("Created timestamp is required");

  // Validate optional fields
  if (profile.age !== null && profile.age !== undefined) {
    if (
      typeof profile.age !== "number" ||
      profile.age < 1 ||
      profile.age > 120
    ) {
      errors.push("Age must be a number between 1 and 120");
    }
  }

  if (profile.gender !== null && profile.gender !== undefined) {
    if (!["boy", "girl"].includes(profile.gender)) {
      errors.push("Gender must be 'boy' or 'girl'");
    }
  }

  // Validate settings
  if (!profile.settings) {
    errors.push("Profile settings are required");
  } else {
    if (!profile.settings.favoriteColor?.match(/^#[0-9A-Fa-f]{6}$/)) {
      errors.push("Invalid favorite color format (should be hex color)");
    }
    if (!["light", "dark"].includes(profile.settings.theme)) {
      errors.push("Invalid theme (should be 'light' or 'dark')");
    }
    if (typeof profile.settings.soundEnabled !== "boolean") {
      errors.push("Sound enabled setting should be a boolean");
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Updates a user profile with new data
 * @param {UserProfile} profile - Existing profile
 * @param {Partial<UserProfile>} updates - Profile updates
 * @returns {UserProfile}
 */
export const updateUserProfile = (profile, updates) => {
  const updatedProfile = {
    ...profile,
    ...updates,
    settings: {
      ...profile.settings,
      ...(updates.settings || {}),
    },
    gameProgress: {
      ...profile.gameProgress,
      ...(updates.gameProgress || {}),
    },
  };

  // Ensure we don't override these fields
  updatedProfile.id = profile.id;
  updatedProfile.userId = profile.userId;
  updatedProfile.createdAt = profile.createdAt;

  return updatedProfile;
};

/**
 * Updates game progress for a specific game
 * @param {UserProfile} profile - User profile to update
 * @param {string} gameId - ID of the game
 * @param {Partial<GameProgress>} progress - New progress data
 * @returns {UserProfile}
 */
export const updateGameProgress = (profile, gameId, progress) => {
  return {
    ...profile,
    gameProgress: {
      ...profile.gameProgress,
      [gameId]: {
        ...(profile.gameProgress[gameId] || {}),
        ...progress,
        lastPlayed: new Date().toISOString(),
      },
    },
  };
};
